import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { EventContext } from "@/domains/metrics/context";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { api } from "@/modules/api";
import { executeAiToolkitTool, getAiToolkitSerializedError } from "@/modules/ai-toolkit";
import { electronModule } from "@/modules/electron";
import {
  AiToolkitRequestArgs,
  DesktopShortcutEvent,
  MenuActionEvent,
  OAuthAccessTokenGetRequestArgs,
} from "@/modules/electron/types";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { useAppStore } from "@/store";
import { SettingsTabId } from "@/store/pages/SettingsPageStore/types";
import { observer } from "mobx-react-lite";

export const dispatchDesktopShortcutKeyboardEvent = (
  shortcutEvent: DesktopShortcutEvent
): boolean => {
  if (typeof document === "undefined") {
    return false;
  }

  const target =
    document.activeElement instanceof HTMLElement ? document.activeElement : document.body;
  if (!target) {
    return false;
  }

  const forwardedEvent = new KeyboardEvent("keydown", {
    altKey: shortcutEvent.altKey,
    bubbles: true,
    cancelable: true,
    code: shortcutEvent.code,
    ctrlKey: shortcutEvent.ctrlKey,
    key: shortcutEvent.key,
    metaKey: shortcutEvent.metaKey,
    shiftKey: shortcutEvent.shiftKey,
  });

  target.dispatchEvent(forwardedEvent);
  return true;
};

export const DesktopEventListenersBridge = observer(() => {
  const { store } = useAppStore();

  useEffectOnMount(() => {
    const onTrackedEvent = ({ trackedEvent }: { trackedEvent: TrackedEvent }) => {
      trackEvent(trackedEvent);
    };

    const onMenuActionEvent = ({ actionEvent }: { actionEvent: MenuActionEvent }) => {
      logger.info({
        message: "[DesktopEventListenersBridge] Menu action event received.",
        info: { actionEvent },
      });
      switch (actionEvent) {
        case MenuActionEvent.CREATE_NOTE:
          store.notes.composeNewNote({
            eventContext: EventContext.DesktopMenuAction,
          });
          break;

        case MenuActionEvent.OPEN_SETTINGS:
          store.settingsModal.open(SettingsTabId.CalendarAndMeetingsPage);
          break;
      }
    };

    const onDesktopShortcutEvent = ({ shortcutEvent }: { shortcutEvent: DesktopShortcutEvent }) => {
      logger.info({
        message: "[DesktopEventListenersBridge] Desktop shortcut event received.",
        info: { shortcutEvent },
      });

      dispatchDesktopShortcutKeyboardEvent(shortcutEvent);
    };

    const onOAuthAccessTokenRefreshRequested = () => {
      /**
       * Future work: this should refresh the renderer-owned OAuth access token
       * and push it back to desktop main through electronModule.oauthAccessToken.set.
       */
      logger.error({
        message:
          "[DesktopEventListenersBridge] OAuth access token refresh requested, but refresh is not implemented.",
      });
    };

    const onOAuthAccessTokenGetRequested = ({ requestId }: OAuthAccessTokenGetRequestArgs) => {
      const authToken = api.getAuthToken();
      const responsePromise = electronModule.oauthAccessToken.respondToGetRequest?.({
        requestId,
        token: authToken ?? null,
      });

      void responsePromise?.catch(error => {
        logger.error({
          message: "[DesktopEventListenersBridge] Failed to respond to OAuth access token request.",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      });
    };

    const onAiToolkitRequested = ({ requestId, toolName, input }: AiToolkitRequestArgs) => {
      void (async () => {
        try {
          const output = await executeAiToolkitTool({ store, toolName, input });

          await electronModule.aiToolkit?.respondToRequest?.({
            requestId,
            ok: true,
            output,
          });
        } catch (error) {
          await electronModule.aiToolkit?.respondToRequest?.({
            requestId,
            ok: false,
            error: getAiToolkitSerializedError({ error }),
          });
        }
      })().catch(error => {
        logger.error({
          message: "[DesktopEventListenersBridge] Failed to respond to AI toolkit request.",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      });
    };

    let unsubscribeOnTrackedEvent: (() => void) | undefined;
    let unsubscribeOnMenuActionEvent: (() => void) | undefined;
    let unsubscribeOnDesktopShortcutEvent: (() => void) | undefined;
    let unsubscribeOnOAuthAccessTokenGetRequested: (() => void) | undefined;
    let unsubscribeOnOAuthAccessTokenRefreshRequested: (() => void) | undefined;
    let unsubscribeOnAiToolkitRequested: (() => void) | undefined;

    electronModule
      .onTrackedEvent?.({
        callback: onTrackedEvent,
      })
      .then(unsubscribe => {
        unsubscribeOnTrackedEvent = unsubscribe;
      })
      .catch(error => {
        logger.error({
          message: "[DesktopEventListenersBridge] Failed to subscribe to tracked event.",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      });
    electronModule
      .onMenuActionEvent?.({
        callback: onMenuActionEvent,
      })
      .then(unsubscribe => {
        unsubscribeOnMenuActionEvent = unsubscribe;
      })
      .catch(error => {
        logger.error({
          message: "[DesktopEventListenersBridge] Failed to subscribe to menu action event.",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      });
    electronModule
      .onDesktopShortcutEvent?.({
        callback: onDesktopShortcutEvent,
      })
      .then(unsubscribe => {
        unsubscribeOnDesktopShortcutEvent = unsubscribe;
      })
      .catch(error => {
        logger.error({
          message: "[DesktopEventListenersBridge] Failed to subscribe to desktop shortcut event.",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      });
    electronModule.oauthAccessToken
      .onGetRequested?.({
        callback: onOAuthAccessTokenGetRequested,
      })
      .then(unsubscribe => {
        unsubscribeOnOAuthAccessTokenGetRequested = unsubscribe;
      })
      .catch(error => {
        logger.error({
          message:
            "[DesktopEventListenersBridge] Failed to subscribe to OAuth access token requests.",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      });
    electronModule.oauthAccessToken
      .onRefreshRequested?.({
        callback: onOAuthAccessTokenRefreshRequested,
      })
      .then(unsubscribe => {
        unsubscribeOnOAuthAccessTokenRefreshRequested = unsubscribe;
      })
      .catch(error => {
        logger.error({
          message:
            "[DesktopEventListenersBridge] Failed to subscribe to OAuth access token refresh requests.",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      });
    const aiToolkitSubscriptionPromise = electronModule.aiToolkit?.onRequest?.({
      callback: onAiToolkitRequested,
    });

    if (aiToolkitSubscriptionPromise) {
      aiToolkitSubscriptionPromise
        .then(unsubscribe => {
          unsubscribeOnAiToolkitRequested = unsubscribe;
        })
        .catch(error => {
          logger.error({
            message: "[DesktopEventListenersBridge] Failed to subscribe to AI toolkit requests.",
            info: { error: objectModule.safeErrorAsJson(error as Error) },
          });
        });
    }

    logger.info({
      message: "[DesktopEventListenersBridge] Subscribed to events.",
    });

    return () => {
      logger.info({
        message: "[DesktopEventListenersBridge] Unsubscribing from events.",
      });
      unsubscribeOnTrackedEvent?.();
      unsubscribeOnMenuActionEvent?.();
      unsubscribeOnDesktopShortcutEvent?.();
      unsubscribeOnOAuthAccessTokenGetRequested?.();
      unsubscribeOnOAuthAccessTokenRefreshRequested?.();
      unsubscribeOnAiToolkitRequested?.();
    };
  });

  return null;
});
